import type { ReactNode } from "react";
import { SlideStatement } from "../../../SlideStatement";
import { SupLink } from "../../../SlideContext";

const DIY_MANUAL_URL = "https://www.apptonomy.ai/diy";

type Accent = "blue" | "violet";

interface Moat {
  num: string;
  title: string;
  strap: string;
  body: ReactNode;
  proof?: ReactNode;
}

const NOW_MOATS: Moat[] = [
  {
    num: "1",
    title: "A Rare Founder Combination",
    strap: "Deep ASO practice × Netflix-rooted product craft.",
    body: "A 12-year ASO practitioner who authored the methodology × a product CEO from Netflix, YouTube and MessageBird's $800M Series C.",
    proof:
      "A competitor's webinar exposed the AI-discovery gap; our engine was live 8 days later.",
  },
  {
    num: "2",
    title: "Methodology Head Start",
    strap: "Human expertise, compiled into software.",
    body: (
      <>
        100+ expert prompts and rubrics, tuned daily —{" "}
        <span className="font-semibold text-slate-900">
          109 updates in 180 days.
        </span>
      </>
    ),
    proof:
      "The only platform auditing how apps surface in ChatGPT, Claude, Gemini and Perplexity answers.",
  },
  {
    num: "3",
    title: "Technology Head Start",
    strap: "Every pipe in production, end-to-end.",
    body: (
      <>
        22+ live integrations across both stores —{" "}
        <span className="font-semibold text-slate-900">
          18+ months a competitor must repeat
        </span>
        <SupLink to="capital-efficiency">C</SupLink>, against a moving target.
      </>
    ),
  },
  {
    num: "4",
    title: "Build vs Buy",
    strap: "A platform project, not a prompt.",
    body: (
      <>
        One audit fans out across{" "}
        <span className="font-semibold text-slate-900">
          10 engines and 100+ model calls, over 9 live store surfaces
        </span>{" "}
        — then repeats daily, for every app and every competitor.
      </>
    ),
    proof: (
      <>
        We publish the blueprint at{" "}
        <a
          href={DIY_MANUAL_URL}
          target="_blank"
          rel="noreferrer"
          className="font-semibold underline decoration-blue-300 underline-offset-2 hover:decoration-blue-700"
        >
          apptonomy.ai/diy
        </a>{" "}
        — the blueprint isn't the moat. The upkeep is.
      </>
    ),
  },
];

const EXPANDING_MOATS: Moat[] = [
  {
    num: "5",
    title: "The Audit Corpus",
    strap: "Every audit makes the next audit better.",
    body: (
      <>
        <span className="font-semibold text-slate-900">
          &gt;20,000 audits across 200+ apps since February
        </span>{" "}
        — history that can't be bought, only accrued. Every shipped revision
        adds its measured lift.
      </>
    ),
  },
  {
    num: "6",
    title: "Customer Lock-In",
    strap: "Switching costs compound with tenure.",
    body: "Competitor sets, calibrated rubrics, measured-lift history — leaving means restarting attribution from zero.",
  },
  {
    num: "7",
    title: "Ukko, the factory",
    strap: "The head start compounds nightly.",
    body: (
      <>
        Our agentic software factory ships{" "}
        <span className="font-semibold text-slate-900">
          nightly, unattended &mdash; 1,500+ PRs merged in the last 90 days,
          1M+ lines of code maintained
        </span>
        <SupLink to="ai-native-factory">E</SupLink>.
      </>
    ),
  },
];

export default function MoatTechEdge() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-10 md:[&_header]:mb-0">
      <SlideStatement eyebrow="Moat">
        <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
          Apptonomy is hard to replicate today, and it gets harder every week.
        </h1>
      </SlideStatement>

      <div className="flex flex-col gap-5 md:min-h-0 md:flex-1 md:justify-center md:gap-4 md:pt-6">
        <MoatBand
          label="Now"
          sublabel="Already hard to replicate"
          accent="blue"
          moats={NOW_MOATS}
        />
        <MoatBand
          label="Expanding Moat Over Time"
          sublabel="Compounds with usage & tenure"
          accent="violet"
          moats={EXPANDING_MOATS}
        />
      </div>
    </div>
  );
}

function MoatBand({
  label,
  sublabel,
  accent,
  moats,
}: {
  label: string;
  sublabel: string;
  accent: Accent;
  moats: Moat[];
}) {
  const labelColor = accent === "blue" ? "text-blue-700" : "text-violet-700";
  const gridCols =
    moats.length === 4
      ? "md:grid-cols-4"
      : moats.length === 3
        ? "md:grid-cols-3"
        : "md:grid-cols-2";
  return (
    <div>
      <div className="mb-2 flex items-baseline gap-3 md:mb-2.5">
        <p
          className={`text-xs font-bold uppercase tracking-[0.2em] md:text-sm ${labelColor}`}
        >
          {label}
        </p>
        <p className="text-xs italic text-slate-500 md:text-sm">{sublabel}</p>
        <div className="hidden h-px flex-1 self-center bg-slate-200 md:block" />
      </div>
      <div className={`grid grid-cols-1 gap-3 md:gap-4 ${gridCols}`}>
        {moats.map((m) => (
          <MoatCard key={m.num} moat={m} accent={accent} />
        ))}
      </div>
    </div>
  );
}

function MoatCard({ moat, accent }: { moat: Moat; accent: Accent }) {
  const badgeColor = accent === "blue" ? "bg-blue-600" : "bg-violet-600";
  const titleColor = accent === "blue" ? "text-blue-700" : "text-violet-700";
  return (
    <div className="flex flex-col rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-3.5">
      <div className="flex items-center gap-2.5">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${badgeColor}`}
        >
          {moat.num}
        </span>
        <h3
          className={`text-lg font-bold leading-tight tracking-tight md:text-xl ${titleColor}`}
        >
          {moat.title}
        </h3>
      </div>
      <p className="mt-2 text-sm font-semibold leading-snug text-slate-900 md:text-[15px]">
        {moat.strap}
      </p>
      <p className="mt-1.5 text-xs leading-snug text-slate-600 md:text-[13px]">
        {moat.body}
      </p>
      {moat.proof ? (
        <p className="mt-1.5 text-xs italic leading-snug text-blue-800 md:text-[13px]">
          {moat.proof}
        </p>
      ) : null}
    </div>
  );
}
